import {DataTable} from "../DataTable/DataTable";
import {VehicleEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {Anchor, Box, Modal, ModalProps} from "@mantine/core";
import {Link} from "react-router-dom";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {useVehicleDataTableFilter} from "../VehicleDataTable/useVehicleDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";

const EMPTY_ARRAY: VehicleEntity[] = []

export type VehicleDataTableProps = EntityDataTableProps<VehicleEntity> & {}

export const VehicleDataTable = (props: VehicleDataTableProps) => {
  const {
    records: vehicles = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = useVehicleDataTableFilter({
    records: vehicles
  })

  const sortConfig = useMemo(() => {
    return {
      "name": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.name?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'name', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const columns = useMemo(() => {
    const values: DataTableColumn<VehicleEntity>[] = [
      {
        ...getColumnFilterProps(filterConfig, "name"),
        title: t("common.entity.vehicle.table.column.name", "Name"),
        accessor: 'name',
        width: 100,
        sortable: true,
        render: (record) => (
          <EntityAnchor value={record} />
        ),
      },
      {
        ...getColumnFilterProps(filterConfig, "companyName"),
        title: t("common.entity.vehicle.table.column.company", "Company"),
        accessor: 'companyName',
        width: 100,
        sortable: true,
        render: ({companyId, companyName}) => (
          companyId ? <DefaultText value={companyName}/> : undefined
        )
      },
      {
        ...getColumnFilterProps(filterConfig, "barcode"),
        title: t("common.entity.vehicle.table.column.qrcode", "QR Code"),
        accessor: 'barcode',
        width: 100,
        sortable: true,
      },
    ]

    return values
  }, [filterConfig, t])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}

export type VehicleDataTableModalProps = Omit<ModalProps, "onSubmit"> & {
  dataTable: Omit<VehicleDataTableProps, 'recordsPerPageOptions'>
}

export const VehicleDataTableModal = (props: VehicleDataTableModalProps) => {
  const {t} = useTranslation(["common"]);

  const {
    title = t<string>("common.entity.vehicle.plural", "Vehicles"),
    dataTable,
    ...rest
  } = props;

  return (
    <Modal {...rest} title={title}>
      <Box>
        <VehicleDataTable
          {...dataTable}
          recordsPerPageOptions={[5]}/>
      </Box>
    </Modal>
  );
};
